import { es } from './i18n/es';
import { en } from './i18n/en';

// Cada idioma tiene su propia URL (/ y /en/) con su HTML prerenderizado, para
// que Google y los bots que no ejecutan JS vean la versión inglesa de verdad y
// el par hreflang sea coherente (antes /?lng=en servía el HTML en español).
export const LANGS = ['es', 'en'] as const;
export type Lang = (typeof LANGS)[number];

const SITE = 'https://www.bestigium.com';

export const PAGE_PATH: Record<Lang, string> = { es: '/', en: '/en/' };
const OG_LOCALE: Record<Lang, string> = { es: 'es_ES', en: 'en_US' };
const META: Record<Lang, typeof es.meta | typeof en.meta> = { es: es.meta, en: en.meta };

const escapeAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

// Falla en el build si index.html cambia y una etiqueta deja de encontrarse:
// mejor romper el prerender que publicar una página con el <head> a medias.
function replaceOnce(html: string, re: RegExp, value: (m: string, ...g: string[]) => string) {
  if (!re.test(html)) throw new Error(`localizeHead: no se encontró ${re}`);
  return html.replace(re, value);
}

const setMeta = (html: string, attr: 'name' | 'property', key: string, value: string) =>
  replaceOnce(
    html,
    new RegExp(`(<meta\\s+${attr}="${key}"\\s+content=")[^"]*(")`),
    (_m, a, b) => a + escapeAttr(value) + b
  );

/**
 * Adapta el <head> de index.html (que está en español) al idioma `lng`:
 * <html lang>, title, description, canonical, og:* / twitter:* y el JSON-LD
 * con su inLanguage. Los hreflang no se tocan: son los mismos en las dos páginas.
 */
export function localizeHead(html: string, lng: Lang): string {
  const m = META[lng];
  const url = SITE + PAGE_PATH[lng];
  const alternate = LANGS.find((l) => l !== lng)!;

  html = replaceOnce(html, /<html lang="[^"]*">/, () => `<html lang="${lng}">`);
  html = replaceOnce(html, /<title>[^<]*<\/title>/, () => `<title>${escapeAttr(m.title)}</title>`);
  html = replaceOnce(
    html,
    /(<link rel="canonical" href=")[^"]*(")/,
    (_m, a, b) => a + url + b
  );
  html = setMeta(html, 'name', 'description', m.description);
  html = setMeta(html, 'property', 'og:title', m.title);
  html = setMeta(html, 'property', 'og:description', m.og_description);
  html = setMeta(html, 'property', 'og:url', url);
  html = setMeta(html, 'property', 'og:locale', OG_LOCALE[lng]);
  html = setMeta(html, 'property', 'og:locale:alternate', OG_LOCALE[alternate]);
  html = setMeta(html, 'property', 'og:image:alt', m.og_image_alt);
  html = setMeta(html, 'name', 'twitter:title', m.title);
  html = setMeta(html, 'name', 'twitter:description', m.og_description);

  return replaceOnce(
    html,
    /(<script type="application\/ld\+json">)([\s\S]*?)(<\/script>)/,
    (_m, open, json, close) => {
      const data = JSON.parse(json);
      data.inLanguage = lng;
      data.description = m.ld_description;
      return `${open}\n${JSON.stringify(data, null, 2)}\n${close}`;
    }
  );
}
